import { Container, Graphics, Text } from 'pixi.js';
import { COLORS, textStyle } from './theme';

const BAR_H = 26;

/**
 * GunBound-style horizontal power bar pinned to the bottom of the screen:
 * shows the live drag or charge power with a yellow marker at the team's
 * last shot power. The game lays it out via layout() so it can span the
 * full width on phones (next to the mobile FUSE/weapon buttons) or sit
 * centered on desktop.
 */
export interface PowerBarLastShot {
  angleDeg: number;
  power: number;
}

export class PowerBar {
  container: Container;
  private bg: Graphics;
  private fill: Graphics;
  private marker: Graphics;
  private label: Text;
  private lastText: Text;
  /** User-placed sticky marker (local, persistent; tap to place/clear). */
  private goalMarker: Graphics;
  private goalPower: number | null = null;
  private barW = 320;
  /** Quarter dividers (25/50/75%) drawn above the fill to aid power judging. */
  private dividers: Graphics;

  constructor(screenWidth: number, screenHeight: number) {
    this.container = new Container();
    this.container.visible = false;

    this.bg = new Graphics();
    this.container.addChild(this.bg);

    this.fill = new Graphics();
    this.container.addChild(this.fill);

    // Above the fill so the quarter marks stay visible at any power level.
    this.dividers = new Graphics();
    this.container.addChild(this.dividers);

    this.marker = new Graphics();
    this.container.addChild(this.marker);

    // Power readout lives inside the bar (GunBound slice-bar style).
    this.label = new Text({
      text: 'POWER',
      style: textStyle({ size: 13, weight: '700', display: true, shadow: false }),
    });
    this.label.anchor.set(0.5, 0.5);
    this.container.addChild(this.label);

    // Own last shot memory (private to this player; never shown to enemies).
    this.lastText = new Text({
      text: '',
      style: textStyle({ size: 12, weight: '700', color: COLORS.gold, shadow: false }),
    });
    this.lastText.anchor.set(1, 1);
    this.lastText.visible = false;
    this.container.addChild(this.lastText);

    this.goalMarker = new Graphics();
    this.container.addChild(this.goalMarker);

    this.layout(0, screenWidth, screenHeight);
  }

  /**
   * Quick tap on the bar (mouse or touch): place the goal marker there, or
   * clear it when tapping the marker itself. Local-only and persistent.
   */
  toggleGoalMarker(power: number): void {
    if (this.goalPower !== null && Math.abs(this.goalPower - power) <= 4) {
      this.goalPower = null;
    } else {
      this.goalPower = Math.max(0, Math.min(100, Math.round(power)));
    }
  }

  setGoalMarker(power: number | null): void {
    this.goalPower = power;
  }

  /**
   * Pin the bar to the bottom of the viewport. `reservedLeft` keeps the bar
   * clear of the mobile FUSE/weapon buttons sharing the bottom strip.
   */
  layout(reservedLeft: number, screenWidth: number, screenHeight: number): void {
    const margin = 8;
    let x: number;
    if (reservedLeft > 0) {
      // Mobile: fill the bottom strip to the right of the gamepad buttons.
      x = reservedLeft + margin;
      this.barW = Math.max(160, screenWidth - x - margin);
      this.container.position.set(x, screenHeight - BAR_H - 17);
    } else {
      // Desktop: wide centered bar.
      this.barW = Math.min(640, screenWidth - margin * 2);
      x = (screenWidth - this.barW) / 2;
      this.container.position.set(x, screenHeight - BAR_H - 14);
    }

    this.bg.clear();
    this.bg.roundRect(0, 0, this.barW, BAR_H, 6);
    this.bg.fill({ color: COLORS.bg, alpha: 0.72 });
    this.bg.stroke({ width: 2, color: COLORS.bevelLight, alpha: 0.7 });

    // Split the bar into 4 equal parts; the 50% mark is drawn brighter.
    this.dividers.clear();
    for (let i = 1; i < 4; i++) {
      const dx = 2 + (this.barW - 4) * (i / 4);
      const mid = i === 2;
      this.dividers.moveTo(dx, 2);
      this.dividers.lineTo(dx, BAR_H - 2);
      this.dividers.stroke({ width: mid ? 2 : 1, color: 0xffffff, alpha: mid ? 0.7 : 0.4 });
    }

    this.label.position.set(this.barW / 2, BAR_H / 2);
    this.lastText.position.set(this.barW - 4, -6);
  }

  show(): void {
    this.container.visible = true;
  }

  hide(): void {
    this.container.visible = false;
  }

  isVisible(): boolean {
    return this.container.visible;
  }

  /** Hit test in screen coordinates (generous margins for touch). */
  containsPoint(screenX: number, screenY: number): boolean {
    const { x, y } = this.container.position;
    return (
      screenX >= x - 10 &&
      screenX <= x + this.barW + 10 &&
      screenY >= y - 16 &&
      screenY <= y + BAR_H + 16
    );
  }

  /** Power (0-100) for a horizontal screen position over the bar. */
  powerForX(screenX: number): number {
    const local = (screenX - this.container.position.x - 2) / (this.barW - 4);
    return Math.round(Math.max(0, Math.min(100, local * 100)));
  }

  update(power: number, lastShot: PowerBarLastShot | null): void {
    const p = Math.max(0, Math.min(100, power));

    this.fill.clear();
    if (p > 0) {
      const w = (this.barW - 4) * (p / 100);
      const r = Math.round(255 * Math.min(1, p / 50));
      const g = Math.round(255 * Math.max(0, 1 - p / 100));
      this.fill.roundRect(2, 2, w, BAR_H - 4, 4);
      this.fill.fill({ color: (r << 16) | (g << 8), alpha: 0.9 });
    }
    this.label.text = `POWER ${Math.round(p)}`;

    this.marker.clear();
    this.lastText.visible = lastShot !== null;
    if (lastShot) {
      const mx = 2 + (this.barW - 4) * (Math.max(0, Math.min(100, lastShot.power)) / 100);
      this.marker.moveTo(mx, -4);
      this.marker.lineTo(mx, BAR_H + 4);
      this.marker.stroke({ width: 2, color: 0xffee44, alpha: 0.95 });
      this.lastText.text = `LAST ${lastShot.angleDeg}° @ ${lastShot.power}`;
    }

    this.goalMarker.clear();
    if (this.goalPower !== null) {
      const gx = 2 + (this.barW - 4) * (this.goalPower / 100);
      this.goalMarker.moveTo(gx, -6);
      this.goalMarker.lineTo(gx, BAR_H + 6);
      this.goalMarker.stroke({ width: 3, color: 0x55ff55, alpha: 0.9 });
      this.goalMarker.poly([gx - 5, -6, gx + 5, -6, gx, 0]);
      this.goalMarker.fill({ color: 0x55ff55, alpha: 0.9 });
    }
  }

  destroy(): void {
    this.container.destroy({ children: true });
  }
}
